/* eslint-disable import/prefer-default-export */
/* eslint-disable camelcase */
import {
    ARCHITECTURE_1oo1,
    ARCHITECTURE_1oo2,
    ARCHITECTURE_1oo2D,
    ARCHITECTURE_2oo2,
    ARCHITECTURE_2oo3,
    PFD,
    PFH,
} from "const"
import {
    SilCalculationType,
    SilArchitecture,
} from "client/utils"


interface Iec_61508_1998 {
    calcType: SilCalculationType
    architecture: SilArchitecture
    mttr: number
    beta: number
    t1: number
    sd: number
    dd: number
    du: number
}

// "**" (double "*") is an equivalent of "Math.pow"
export const iec_61508_1998_PFD = ({
    calcType,
    architecture,
    mttr,
    beta,
    t1,
    sd,
    dd,
    du,
}: Iec_61508_1998) => {
    const d = du + dd
    const tde = TDE({ mttr, t1, du, dd, d })
    const tse = TSE({ mttr, t1, du, dd, d })

    if (architecture === ARCHITECTURE_1oo1) {
        if (calcType === PFD) {
            return (du + dd) * tde
        }
        if (calcType === PFH) {
            return du
        }
    }

    if (architecture === ARCHITECTURE_1oo2) {
        if (calcType === PFD) {
            return 2 * ((1 - beta) * dd + (1 - 2 * beta) * du) ** 2 * tde * tse + beta * dd * mttr + 2 * beta * du * (t1 / 2 + mttr)
        }
        if (calcType === PFH) {
            return 2 * ((1 - beta) * dd + (1 - 2 * beta) * du) ** 2 * tde + beta * dd + 2 * beta * du
        }
    }

    if (architecture === ARCHITECTURE_2oo2) {
        if (calcType === PFD) {
            return 2 * d * tde
        }
        if (calcType === PFH) {
            return 2 * du
        }
    }

    if (architecture === ARCHITECTURE_1oo2D) {
        const tdePrime = TDEPrime({ mttr, t1, sd, dd, du })
        const tsePrime = TSEPrime({ mttr, t1, sd, dd, du })
        const td = TD({ mttr, t1, sd, dd, du })

        const rt = 2 * (1 - 2 * beta) * du * ((1 - 2 * beta) * du + (1 - beta) * dd + sd)
        if (calcType === PFD) {
            return rt * tdePrime * tsePrime + beta * dd * mttr + 2 * beta * du * (t1 / 2 + mttr)
        }
        if (calcType === PFH) {
            return rt * td + beta * dd + 2 * beta * du
        }
    }

    if (architecture === ARCHITECTURE_2oo3) {
        const rt = 6 * ((1 - beta) * dd + (1 - 2 * beta) * du) ** 2
        if (calcType === PFD) {
            return rt * tde * tse + beta * dd * mttr + 2 * beta * du * (t1 / 2 + mttr)
        }
        if (calcType === PFH) {
            return rt * tde + beta * dd + 2 * beta * du
        }
    }

    return null
}


// used formulas
interface TDEProps {
    mttr: number
    t1: number
    du: number
    dd: number
    d: number
}
export const TDE = ({ mttr, t1, du, dd, d }: TDEProps) => {
    const tde1 = t1 / 2 + mttr
    return (du / d) * tde1 + (dd / d) * mttr
}


interface TSEProps {
    mttr: number
    t1: number
    du: number
    dd: number
    d: number
}
export const TSE = ({ mttr, t1, du, dd, d }: TSEProps) => {
    const tse1 = t1 / 3 + mttr
    return (du / d) * tse1 + (dd / d) * mttr
}


interface TDEPrimeProps {
    mttr: number
    t1: number
    sd: number
    dd: number
    du: number
}
export const TDEPrime = ({ mttr, t1, sd, dd, du }: TDEPrimeProps) => {
    const tde1 = t1 / 2 + mttr
    const tde2 = dd + sd
    const tde3 = du + dd + sd
    return (du * tde1 + tde2 * mttr) / tde3
}


interface TSEPrimeProps {
    mttr: number
    t1: number
    sd: number
    dd: number
    du: number
}
export const TSEPrime = ({ mttr, t1, sd, dd, du }: TSEPrimeProps) => {
    const tse1 = t1 / 3 + mttr
    const tse2 = dd + sd
    const tse3 = du + dd + sd
    return (du * tse1 + tse2 * mttr) / tse3
}


interface TDProps {
    mttr: number
    t1: number
    sd: number
    dd: number
    du: number
}
export const TD = ({ mttr, t1, sd, dd, du }: TDProps) => {
    const td1 = t1 / 2 + mttr
    const td2 = dd + sd
    const td3 = du + dd + sd
    return (du * td1 + td2 * mttr) / td3
}
